import React, {useState, useRef, useEffect} from "react";
import {motion} from "framer-motion";

function generateSwissMatchups(players, pastMatchups, groupSize) {
    const sorted = [...players].sort((a, b) => b.score - a.score);
    const matchups = [];
    const used = new Set();

    function hasPlayed(p1, p2) {
        return pastMatchups.some((round) => Array.isArray(round) && round.some((player) => player.name === p1) && round.some((player) => player.name === p2));
    }

    for (let i = 0; i < sorted.length; i++) {
        if (used.has(sorted[i].name)) continue;
        const group = [sorted[i]];
        used.add(sorted[i].name);
        for (let j = i + 1; j < sorted.length && group.length < groupSize; j++) {
            if (!used.has(sorted[j].name) && !group.some(p => hasPlayed(p.name, sorted[j].name))) {
                group.push(sorted[j]);
                used.add(sorted[j].name);
            }
        }
        for (let j = i + 1; j < sorted.length && group.length < groupSize; j++) {
            if (!used.has(sorted[j].name)) {
                group.push(sorted[j]);
                used.add(sorted[j].name);
            }
        }
        matchups.push(group.map(player => ({...player, placement: undefined})));
    }

    return matchups;
}

function TournamentApp() {
    const [players, setPlayers] = useState([]);
    const [playerName, setPlayerName] = useState("");
    const [groupSize, setGroupSize] = useState(4);
    const [currentRound, setCurrentRound] = useState(0);
    const [matchups, setMatchups] = useState([]);
    const [started, setStarted] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [playerName]);

    const addPlayer = () => {
        if (playerName.trim()) {
            setPlayers([...players, {name: playerName, score: 0}]);
            setPlayerName("");
        }
    };

    const removePlayer = (name) => {
        setPlayers(players.filter((p) => p.name !== name));
    };

    const movePlayer = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= players.length) return;
        const newPlayers = [...players];
        [newPlayers[index], newPlayers[newIndex]] = [newPlayers[newIndex], newPlayers[index]];
        setPlayers(newPlayers);
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            addPlayer();
        }
    };

    const startTournament = () => {
        setStarted(true);
        const initialMatchups = generateSwissMatchups(players, [], groupSize);
        setMatchups([initialMatchups]);
    };

    const resetTournament = () => {
        setStarted(false);
        setCurrentRound(0);
        setMatchups([]);
        setPlayers(players.map((p) => ({...p, score: 0})));
    };

    const allPlacementsSet = () => {
        return matchups[currentRound].every(group => group.every(player => player.placement !== undefined));
    };

    const nextRound = () => {
        if (!allPlacementsSet()) return;
        const newMatchups = generateSwissMatchups(players, matchups.flat(), groupSize);
        setMatchups([...matchups, newMatchups]);
        setCurrentRound(currentRound + 1);
    };

    const setPlacement = (groupIndex, playerIndex, placement) => {
        const newMatchups = [...matchups];
        const group = [...newMatchups[currentRound][groupIndex]];
        const updatedPlayers = [...players];
        const player = group[playerIndex];
        const currentPlacement = player.placement;
        const playerInList = updatedPlayers.find(p => p.name === player.name);
        if (!playerInList) return;

        const groupPoints = Array.from({length: groupSize}, (_, i) => groupSize - i);

        if (currentPlacement !== undefined) {
            playerInList.score -= groupPoints[currentPlacement];
        }

        if (currentPlacement === placement) {
            player.placement = undefined;
        } else {
            player.placement = placement;
            playerInList.score += groupPoints[placement];
        }

        newMatchups[currentRound][groupIndex] = group;
        setMatchups(newMatchups);
        setPlayers(updatedPlayers);
    };

    const isPlacementUsed = (group, placement) => {
        return group.some(player => player.placement === placement);
    };

    return (
        <div className="p-6 space-y-6 bg-gray-900 text-white min-h-screen">
            <h1 className="text-3xl font-bold">Swiss Tournament Generator</h1>

            {!started && (
                <div className="space-y-4">
                    <div className="flex gap-2">
                        <input
                            ref={inputRef}
                            value={playerName}
                            onChange={(e) => setPlayerName(e.target.value)}
                            onKeyDown={handleKeyPress}
                            placeholder="Player name"
                            className="bg-gray-800 text-white placeholder-gray-400 px-2 py-1 rounded"
                        />
                        <button className="bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded" onClick={addPlayer}>Add
                            Player
                        </button>
                    </div>
                    <ul>
                        {players.map((player, idx) => (
                            <li key={idx} className="flex justify-between items-center w-64">
                                <span>{player.name}</span>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => movePlayer(idx, -1)}
                                        className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50"
                                        disabled={idx === 0}>⬆️
                                    </button>
                                    <button
                                        onClick={() => movePlayer(idx, 1)}
                                        className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded disabled:opacity-50"
                                        disabled={idx === players.length - 1}>⬇️
                                    </button>
                                    <button className="bg-red-600 hover:bg-red-700 px-2 py-1 rounded"
                                            onClick={() => removePlayer(player.name)}>❌
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <div className="flex gap-4">
                        <div>
                            <label>Players per Matchup: </label>
                            <input
                                type="number"
                                value={groupSize}
                                min={2}
                                max={players.length}
                                onChange={(e) => setGroupSize(Number(e.target.value))}
                                className="w-20 bg-gray-800 text-white px-2 py-1 rounded"
                            />
                        </div>
                    </div>
                    <button className="bg-green-600 hover:bg-green-700 px-3 py-1 rounded" onClick={startTournament}
                            disabled={players.length < 2}>
                        Start Tournament
                    </button>
                </div>
            )}

            {started && (
                <motion.div className="space-y-4">
                    <h2 className="text-xl font-semibold">Round {currentRound + 1}</h2>
                    {matchups[currentRound].map((group, groupIdx) => (
                        <div key={groupIdx} className="p-4 bg-gray-800 rounded">
                            <div className="grid grid-cols-2 gap-2">
                                {group.map((player, i) => (
                                    <div key={i} className="flex flex-col items-start space-y-1">
                                        <span className="font-medium text-white">{player.name}</span>
                                        <div className="flex gap-1">
                                            {Array.from({length: group.length}, (_, placementIdx) => {
                                                const isSelected = player.placement === placementIdx;
                                                const alreadyUsed = isPlacementUsed(group, placementIdx);
                                                const disabled = !isSelected && alreadyUsed;
                                                return (
                                                    <button
                                                        key={placementIdx}
                                                        className={`px-2 py-1 rounded ${
                                                            isSelected ? "bg-blue-600 hover:bg-blue-700" : disabled ? "bg-gray-600 text-gray-400 cursor-not-allowed" : "bg-gray-700 text-white hover:bg-gray-600"
                                                        }`}
                                                        disabled={disabled}
                                                        onClick={() => setPlacement(groupIdx, i, placementIdx)}
                                                    >
                                                        #{placementIdx + 1}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    <div className="flex gap-4">
                        <button className="bg-purple-600 hover:bg-purple-700 px-3 py-1 rounded" onClick={nextRound}
                                disabled={!allPlacementsSet()}>
                            Next Round
                        </button>
                        <button className="bg-red-600 hover:bg-red-700 px-3 py-1 rounded"
                                onClick={resetTournament}>Reset Tournament
                        </button>
                    </div>
                </motion.div>
            )}

            <div>
                <h2 className="text-xl font-semibold">Leaderboard</h2>
                <ul>
                    {[...players].sort((a, b) => b.score - a.score).map((player, idx) => (
                        <li key={idx} className="text-white">
                            {player.name}: {player.score}
                        </li>
                    ))}
                </ul>

                <h3 className="text-lg font-semibold mt-4">Matchup Results</h3>
                {matchups.map((round, rIdx) => (
                    <div key={rIdx} className="mt-2">
                        <strong>Round {rIdx + 1}</strong>
                        <ul>
                            {round.map((group, gIdx) => (
                                <li key={gIdx} className="text-white">
                                    Matchup {gIdx + 1}: {group
                                    .filter(p => p.placement !== undefined)
                                    .sort((a, b) => a.placement - b.placement)
                                    .map((p) => `${p.name} (${groupSize - p.placement})`)
                                    .join(", ")}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default TournamentApp;
